import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, GraduationCap, Award, ChevronRight, Activity, Zap, Target, Move, Gauge, ShieldCheck } from 'lucide-react';
export function About() {
  return <div className="w-full" data-id="element-91">
      <SplitHero data-id="element-92" />
      <StatsBand data-id="element-93" />
      <BioNarrative data-id="element-94" />
      <CredentialsStrip data-id="element-95" />
      <Philosophy data-id="element-96" />
      <Specialties data-id="element-97" />
      <SportsLevels data-id="element-98" />
      <MidCta data-id="element-99" />
      <Facility data-id="element-100" />
      <FinalCta data-id="element-101" />
    </div>;
}
/* ---------- MID-PAGE CTA ---------- */
function MidCta() {
  return <section className="bg-reach-red" data-id="element-102">
      <div className="max-w-container mx-auto px-4 md:px-8 py-10 md:py-12 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left" data-id="element-103">
        <div data-id="element-104">
          <p className="font-bebas text-white text-3xl md:text-4xl tracking-wide leading-none mb-1" data-id="element-105">
            Train with Reach.
          </p>
          <p className="font-inter text-white/90 text-sm md:text-base uppercase tracking-[0.2em]" data-id="element-106">
            Start with a free 15-minute consultation.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 shrink-0 w-full sm:w-auto" data-id="element-107">
          <Link to="/book" className="inline-flex items-center justify-center bg-white text-reach-black font-inter font-semibold px-6 min-h-[44px] md:min-h-[48px] rounded-[4px] transition-all duration-200 ease-out hover:bg-reach-black hover:text-white uppercase tracking-wide text-sm hover:-translate-y-[3px]" data-id="element-108">
            Book Consultation
          </Link>
          <Link to="/pricing" className="btn-secondary-dark sm:w-auto" data-id="element-109">
            See Pricing
          </Link>
        </div>
      </div>
    </section>;
}
/* ---------- BIO NARRATIVE ---------- */
function BioNarrative() {
  return <section className="bg-white py-20 md:py-28" data-id="element-110">
      <div className="max-w-3xl mx-auto px-4 md:px-8" data-id="element-111">
        <div className="text-center mb-12" data-id="element-112">
          <p className="font-inter text-reach-red text-xs uppercase tracking-[0.3em] font-semibold mb-3" data-id="element-113">
            Background
          </p>
          <h2 className="font-bebas text-5xl md:text-6xl tracking-wide leading-none" data-id="element-114">
            The Story
          </h2>
          <div className="w-12 h-1 bg-reach-red mt-6 mx-auto" data-id="element-115" />
        </div>

        <div className="space-y-6 font-inter text-reach-black text-base md:text-lg leading-[1.75]" data-id="element-116">
          <p data-id="element-117">
            <span className="float-left font-bebas text-reach-red text-[72px] leading-[0.85] mr-3 mt-1" data-id="element-118">
              F
            </span>
            ernando Beltran is the Owner and Director of Performance at Reach
            Performance, operating in the Dallas–Fort Worth area. He holds a
            Bachelor's Degree in Exercise Science from North Park University and
            a Master's Degree in Business Administration, with nearly a decade
            in strength and conditioning across the youth, high school,
            collegiate, and professional levels.
          </p>

          <p data-id="element-119">
            Before founding Reach, he built his coaching foundation inside
            collegiate strength and conditioning environments:{' '}
            <span className="font-semibold" data-id="element-120">
              Northwestern University Football
            </span>{' '}
            (NCAA Division I),{' '}
            <span className="font-semibold" data-id="element-121">North Park University</span> (NCAA
            Division III), and{' '}
            <span className="font-semibold" data-id="element-122">Florida Memorial University</span>{' '}
            (NAIA), sharpening an approach grounded in performance training,
            athlete development, and speed-based programming.
          </p>

          {/* Pull quote */}
          <blockquote className="my-10 border-l-4 border-reach-red bg-reach-gray pl-6 pr-5 py-6 rounded-r-[4px]" data-id="element-123">
            <p className="font-bebas text-reach-black text-2xl md:text-3xl tracking-wide leading-tight" data-id="element-124">
              Hundreds of athletes coached, from youth players in their first
              season to professionals competing in the{' '}
              <span className="text-reach-red" data-id="element-125">MLB, NFL, NBA</span>, and pro
              soccer.
            </p>
          </blockquote>

          <p data-id="element-126">
            The philosophy combines evidence-based principles with practical
            application. Areas of focus include{' '}
            <span className="font-semibold" data-id="element-127">
              speed development, power training, movement efficiency, and force
              production
            </span>
            , with an emphasis on acceleration mechanics, multidirectional
            speed, strength, deceleration control, and movement quality to build
            explosive, resilient athletes.
          </p>

          <p data-id="element-128">
            Every program also integrates{' '}
            <span className="font-semibold" data-id="element-129">
              athlete monitoring, performance testing, and data-driven
              evaluation
            </span>{' '}
            to individualize training and track development over time. The goal:
            an elite environment that develops athletes physically and mentally,
            preparing them for long-term success in sport, and beyond it.
          </p>
        </div>
      </div>
    </section>;
}
/* ---------- SPLIT HERO ---------- */
function SplitHero() {
  return <section className="bg-white" data-id="element-130">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[600px] lg:min-h-[680px]" data-id="element-131">
        <div className="relative bg-reach-black overflow-hidden order-2 lg:order-1 min-h-[400px] lg:min-h-0" data-id="element-132">
          <img src="https://images.unsplash.com/photo-1599058917212-d750089bc07e?q=80&w=1600&auto=format&fit=crop" alt="Fernando Beltran coaching an athlete" className="absolute inset-0 w-full h-full object-cover photo-editorial" data-id="element-133" />
          <div className="absolute inset-0 bg-gradient-to-tr from-black/55 via-transparent to-black/20" data-id="element-134" />
          <div className="absolute inset-0 bg-reach-red mix-blend-multiply opacity-[0.08]" data-id="element-135" />

          <div className="absolute bottom-12 -left-10 w-[55%] h-1.5 bg-reach-red rotate-[8deg] opacity-70" data-id="element-136" />
          <div className="absolute bottom-8 -left-10 w-[40%] h-1.5 bg-reach-red rotate-[8deg] opacity-40" data-id="element-137" />
          <div className="absolute bottom-4 -left-10 w-[28%] h-1.5 bg-reach-red rotate-[8deg] opacity-25" data-id="element-138" />

          <div className="absolute bottom-6 left-6 right-6 lg:bottom-10 lg:left-10 lg:right-10 z-10" data-id="element-139">
            <p className="font-inter text-white/80 text-xs uppercase tracking-[0.3em] font-semibold" data-id="element-140">
              On the floor · INPOWERiQ
            </p>
          </div>
        </div>

        <div className="flex flex-col justify-center px-6 md:px-12 lg:px-16 py-16 lg:py-0 order-1 lg:order-2" data-id="element-141">
          <p className="font-inter text-reach-red text-xs uppercase tracking-[0.3em] font-semibold mb-4" data-id="element-142">
            Owner · Director of Performance
          </p>
          <h1 className="font-bebas text-reach-black text-6xl md:text-7xl lg:text-[96px] leading-[0.9] tracking-wide mb-6" data-id="element-143">
            Fernando
            <br data-id="element-144" />
            Beltran
          </h1>

          <div className="w-12 h-1 bg-reach-red mb-6" data-id="element-145" />

          <div className="space-y-4 font-inter text-reach-black text-base md:text-lg leading-relaxed max-w-xl" data-id="element-146">
            <p data-id="element-147">
              Built to give Dallas–Fort Worth athletes a true performance
              environment. Structured, measurable, and grounded in the science
              of how athletes actually develop.
            </p>
            <p data-id="element-148">
              Nearly a decade in strength and conditioning, with coaching
              experience across youth, high school, collegiate, and professional
              levels.
            </p>
            <p className="font-bebas text-reach-red text-2xl md:text-3xl tracking-wide pt-2" data-id="element-149">
              "Every day has a purpose."
            </p>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center text-sm" data-id="element-150">
            <a href="mailto:Fernando@reachperformancehq.com" className="inline-flex items-center gap-2 font-inter font-semibold uppercase tracking-wider text-reach-black hover:text-reach-red transition-colors" data-id="element-151">
              <Mail size={16} className="text-reach-red" data-id="element-152" />
              Email Fernando
            </a>
            <span className="hidden sm:inline text-reach-black/30" data-id="element-153">·</span>
            <a href="tel:7089804666" className="inline-flex items-center gap-2 font-inter font-semibold uppercase tracking-wider text-reach-black hover:text-reach-red transition-colors" data-id="element-154">
              <Phone size={16} className="text-reach-red" data-id="element-155" />
              (708) 980-4666
            </a>
          </div>
        </div>
      </div>
    </section>;
}
/* ---------- CREDENTIALS STRIP ---------- */
function CredentialsStrip() {
  return <section className="bg-reach-black text-white py-16 md:py-20 relative overflow-hidden" data-id="element-156">
      <div className="absolute -right-40 top-1/4 w-[40%] h-1 bg-reach-red opacity-30 rotate-[5deg]" data-id="element-157" />
      <div className="absolute -right-40 top-[34%] w-[28%] h-1 bg-reach-red opacity-20 rotate-[5deg]" data-id="element-158" />

      <div className="max-w-container mx-auto px-4 md:px-8 relative" data-id="element-159">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16" data-id="element-160">
          {/* Education */}
          <div data-id="element-161">
            <p className="font-inter text-reach-red text-xs uppercase tracking-[0.3em] font-semibold mb-4 flex items-center gap-2" data-id="element-162">
              <GraduationCap size={16} data-id="element-163" />
              Education
            </p>
            <ul className="space-y-4" data-id="element-164">
              <li className="border-l-2 border-reach-red pl-4" data-id="element-165">
                <p className="font-bebas text-2xl tracking-wide leading-none mb-1" data-id="element-166">
                  Master of Business Administration
                </p>
                <p className="font-inter text-sm text-white/70 uppercase tracking-wider" data-id="element-167">
                  MBA
                </p>
              </li>
              <li className="border-l-2 border-white/20 pl-4" data-id="element-168">
                <p className="font-bebas text-2xl tracking-wide leading-none mb-1" data-id="element-169">
                  Bachelor of Science, Exercise Science
                </p>
                <p className="font-inter text-sm text-white/70 uppercase tracking-wider" data-id="element-170">
                  North Park University
                </p>
              </li>
            </ul>
          </div>

          {/* Coaching Background */}
          <div data-id="element-171">
            <p className="font-inter text-reach-red text-xs uppercase tracking-[0.3em] font-semibold mb-4 flex items-center gap-2" data-id="element-172">
              <Award size={16} data-id="element-173" />
              Coaching Background
            </p>
            <ul className="space-y-4" data-id="element-174">
              <li className="border-l-2 border-reach-red pl-4" data-id="element-175">
                <p className="font-bebas text-xl tracking-wide leading-none mb-1" data-id="element-176">
                  Northwestern University Football
                </p>
                <p className="font-inter text-sm text-white/70 uppercase tracking-wider" data-id="element-177">
                  NCAA Division I
                </p>
              </li>
              <li className="border-l-2 border-white/20 pl-4" data-id="element-178">
                <p className="font-bebas text-xl tracking-wide leading-none mb-1" data-id="element-179">
                  North Park University
                </p>
                <p className="font-inter text-sm text-white/70 uppercase tracking-wider" data-id="element-180">
                  NCAA Division III
                </p>
              </li>
              <li className="border-l-2 border-white/20 pl-4" data-id="element-181">
                <p className="font-bebas text-xl tracking-wide leading-none mb-1" data-id="element-182">
                  Florida Memorial University
                </p>
                <p className="font-inter text-sm text-white/70 uppercase tracking-wider" data-id="element-183">
                  NAIA
                </p>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>;
}
/* ---------- PHILOSOPHY ---------- */
function Philosophy() {
  return <section className="bg-reach-gray py-20 md:py-28" data-id="element-184">
      <div className="max-w-3xl mx-auto px-4 md:px-8" data-id="element-185">
        <div className="text-center mb-12" data-id="element-186">
          <p className="font-inter text-reach-red text-xs uppercase tracking-[0.3em] font-semibold mb-3" data-id="element-187">
            The Philosophy
          </p>
          <h2 className="font-bebas text-5xl md:text-6xl tracking-wide leading-none" data-id="element-188">
            Why We Exist
          </h2>
        </div>

        <div className="space-y-6 font-inter text-reach-black text-lg leading-relaxed" data-id="element-189">
          <p data-id="element-190">
            Reach Performance combines evidence-based training principles with
            practical application that directly transfers to sport. No random
            workouts. No cookie-cutter programs. Every athlete gets training
            that matches their sport, position, and stage of development.
          </p>
          <p data-id="element-191">
            Programming is periodized. Each week, each block, each season has a
            specific purpose. Group sizes are capped at eight so every athlete
            gets coached, not just counted. Performance testing happens every
            four weeks so progress is real, measured, and undeniable.
          </p>
          <p data-id="element-192">
            Athletes leave the floor knowing exactly why they did what they did,
            and how it makes them better on the field, the court, or the mound.
          </p>
        </div>
      </div>
    </section>;
}
/* ---------- SPECIALTIES ---------- */
const SPECIALTIES = [{
  Icon: Zap,
  title: 'Speed Development',
  body: 'Acceleration mechanics, top-end speed, and sprint patterning.'
}, {
  Icon: Target,
  title: 'Power Training',
  body: 'Explosive force expression: jumps, throws, change of direction.'
}, {
  Icon: Move,
  title: 'Multidirectional Speed',
  body: 'Lateral mechanics, cut technique, and reactive agility.'
}, {
  Icon: ShieldCheck,
  title: 'Deceleration Control',
  body: 'Braking force, joint integrity, and injury resilience.'
}, {
  Icon: Gauge,
  title: 'Force Production',
  body: 'Strength under load with transfer to sport-specific output.'
}, {
  Icon: Activity,
  title: 'Movement Quality',
  body: 'Mechanics, positions, and movement efficiency that hold up under fatigue.'
}];
function Specialties() {
  return <section className="bg-white py-20 md:py-28" data-id="element-193">
      <div className="max-w-container mx-auto px-4 md:px-8" data-id="element-194">
        <div className="text-center mb-14" data-id="element-195">
          <p className="font-inter text-reach-red text-xs uppercase tracking-[0.3em] font-semibold mb-3" data-id="element-196">
            Areas of Focus
          </p>
          <h2 className="font-bebas text-4xl md:text-6xl tracking-wide leading-none" data-id="element-197">
            What We Train
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6" data-id="element-198">
          {SPECIALTIES.map(({
          Icon,
          title,
          body
        }) => <div key={title} className="group bg-reach-gray rounded-[4px] p-7 md:p-8 border-b-4 border-transparent hover:border-reach-red hover:-translate-y-1 transition-all duration-200 ease-out flex flex-col" data-id="element-199">
              <div className="w-11 h-11 bg-reach-black rounded-[4px] flex items-center justify-center mb-5" data-id="element-200">
                <Icon size={20} className="text-reach-red" data-id="element-201" />
              </div>
              <h3 className="font-bebas text-2xl tracking-wide uppercase leading-none mb-3" data-id="element-202">
                {title}
              </h3>
              <p className="font-inter text-reach-black/80 text-base leading-relaxed" data-id="element-203">
                {body}
              </p>
            </div>)}
        </div>
      </div>
    </section>;
}
/* ---------- SPORTS & LEVELS ---------- */
const SPORTS = ['Football', 'Baseball', 'Basketball', 'Soccer', 'Volleyball', 'Golf', 'Tennis'];
const LEVELS = [{
  label: 'Youth',
  sub: 'Ages 8–10'
}, {
  label: 'Middle School',
  sub: 'Ages 11–13'
}, {
  label: 'High School',
  sub: 'Varsity & Recruiting'
}, {
  label: 'Collegiate',
  sub: 'NCAA · NAIA'
}, {
  label: 'Professional',
  sub: 'MLB · NFL · NBA · Pro Soccer'
}];
function SportsLevels() {
  return <section className="bg-reach-black text-white py-20 md:py-28 relative overflow-hidden" data-id="element-204">
      <div className="absolute top-0 -left-20 w-[35%] h-1 bg-reach-red rotate-[4deg] opacity-40" data-id="element-205" />
      <div className="absolute bottom-12 -right-20 w-[35%] h-1 bg-reach-red rotate-[4deg] opacity-40" data-id="element-206" />

      <div className="max-w-container mx-auto px-4 md:px-8 relative" data-id="element-207">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20" data-id="element-208">
          {/* Sports */}
          <div data-id="element-209">
            <p className="font-inter text-reach-red text-xs uppercase tracking-[0.3em] font-semibold mb-3" data-id="element-210">
              Sports Trained
            </p>
            <h2 className="font-bebas text-4xl md:text-5xl tracking-wide leading-none mb-8" data-id="element-211">
              Sport-Agnostic. <br data-id="element-212" />
              <span className="text-reach-red" data-id="element-213">Sport-Specific.</span>
            </h2>
            <div className="flex flex-wrap gap-2.5" data-id="element-214">
              {SPORTS.map(s => <span key={s} className="inline-flex items-center px-4 py-2 border border-white/20 rounded-[4px] font-inter text-sm uppercase tracking-wider hover:border-reach-red hover:text-reach-red transition-colors" data-id="element-215">
                  {s}
                </span>)}
            </div>
            <p className="font-inter text-white/70 text-sm mt-6 leading-relaxed max-w-md" data-id="element-216">
              Across all seven sports, from first-year players to starters
              competing on the biggest stages.
            </p>
          </div>

          {/* Levels */}
          <div data-id="element-217">
            <p className="font-inter text-reach-red text-xs uppercase tracking-[0.3em] font-semibold mb-3" data-id="element-218">
              Levels of Experience
            </p>
            <h2 className="font-bebas text-4xl md:text-5xl tracking-wide leading-none mb-8" data-id="element-219">
              Youth to Pro.
            </h2>
            <ul className="space-y-3" data-id="element-220">
              {LEVELS.map(l => <li key={l.label} className="flex items-center justify-between gap-4 border-b border-white/10 pb-3" data-id="element-221">
                  <div className="flex items-center gap-3" data-id="element-222">
                    <ChevronRight size={16} className="text-reach-red shrink-0" data-id="element-223" />
                    <span className="font-bebas text-xl tracking-wider uppercase" data-id="element-224">
                      {l.label}
                    </span>
                  </div>
                  <span className="font-inter text-xs uppercase tracking-[0.2em] text-white/60 text-right" data-id="element-225">
                    {l.sub}
                  </span>
                </li>)}
            </ul>
          </div>
        </div>
      </div>
    </section>;
}
/* ---------- FACILITY ---------- */
function Facility() {
  const photos = [{
    src: 'https://images.unsplash.com/photo-1540497077202-7c8a3999166f?q=80&w=1200&auto=format&fit=crop',
    alt: 'Indoor training turf with speed gates',
    caption: 'Indoor Turf'
  }, {
    src: 'https://images.unsplash.com/photo-1534438097545-a31885568541?q=80&w=1200&auto=format&fit=crop',
    alt: 'Strength and conditioning equipment',
    caption: 'Strength Floor'
  }, {
    src: 'https://images.unsplash.com/photo-1545205597-3d9d02c29597?q=80&w=1200&auto=format&fit=crop',
    alt: 'On-site recovery zone',
    caption: 'Recovery Zone'
  }];
  return <section className="bg-white py-20 md:py-28" data-id="element-226">
      <div className="max-w-container mx-auto px-4 md:px-8" data-id="element-227">
        <div className="text-center mb-12" data-id="element-228">
          <p className="font-inter text-reach-red text-xs uppercase tracking-[0.3em] font-semibold mb-3" data-id="element-229">
            The Facility
          </p>
          <h2 className="font-bebas text-4xl md:text-6xl tracking-wide leading-none" data-id="element-230">
            INPOWERiQ Performance Center
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 mb-12" data-id="element-231">
          {photos.map(p => <figure key={p.caption} className="group relative aspect-[4/5] bg-reach-black rounded-[4px] overflow-hidden" data-id="element-232">
              <img src={p.src} alt={p.alt} className="absolute inset-0 w-full h-full object-cover photo-editorial transition-transform duration-500 group-hover:scale-105" data-id="element-233" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" data-id="element-234" />
              <div className="absolute top-4 left-4 w-10 h-1 bg-reach-red" data-id="element-235" />
              <figcaption className="absolute bottom-5 left-5 right-5" data-id="element-236">
                <p className="font-bebas text-white text-2xl md:text-3xl tracking-wide leading-none" data-id="element-237">
                  {p.caption}
                </p>
              </figcaption>
            </figure>)}
        </div>

        <p className="font-inter text-reach-black text-lg md:text-xl leading-relaxed text-center max-w-3xl mx-auto" data-id="element-238">
          24,000 sq ft. Speed gates. Indoor turf. On-site recovery. Athlete
          monitoring and performance testing built into every program.
        </p>

        <div className="text-center mt-8" data-id="element-239">
          <a href="https://maps.google.com/?q=6051+Alma+Rd,+McKinney,+TX+75070" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 font-inter font-semibold uppercase tracking-wider text-sm text-reach-black hover:text-reach-red transition-colors" data-id="element-240">
            6051 Alma Rd · McKinney, TX 75070
            <span className="text-reach-red" data-id="element-241">→</span>
          </a>
        </div>
      </div>
    </section>;
}
/* ---------- STATS BAND ---------- */
function StatsBand() {
  const stats = [{
    num: '10 yrs',
    label: 'Coaching experience'
  }, {
    num: '500+',
    label: 'Athletes coached'
  }, {
    num: '4',
    label: 'Pro leagues represented'
  }, {
    num: '7',
    label: 'Sports specialized'
  }];
  return <section className="bg-reach-gray py-16 md:py-20 relative overflow-hidden border-y border-black/5" data-id="element-242">
      <div className="max-w-container mx-auto px-4 md:px-8 relative" data-id="element-243">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-10 gap-x-6" data-id="element-244">
          {stats.map((s, i) => <div key={s.label} className={`text-center lg:text-left ${i !== 0 ? 'lg:border-l lg:border-black/10 lg:pl-6' : ''}`} data-id="element-245">
              <p className="font-bebas text-reach-red text-5xl md:text-6xl lg:text-7xl leading-none mb-3 tracking-wide" data-id="element-246">
                {s.num}
              </p>
              <p className="font-inter text-reach-black/80 text-xs md:text-sm uppercase tracking-[0.2em]" data-id="element-247">
                {s.label}
              </p>
            </div>)}
        </div>
      </div>
    </section>;
}
/* ---------- FINAL CTA ---------- */
function FinalCta() {
  return <section className="bg-reach-red py-16 md:py-20" data-id="element-248">
      <div className="max-w-container mx-auto px-4 md:px-8 text-center" data-id="element-249">
        <h2 className="font-bebas text-white text-5xl md:text-7xl tracking-wide mb-4" data-id="element-250">
          Train With Purpose.
        </h2>
        <p className="font-inter text-white text-lg md:text-xl mb-10 max-w-2xl mx-auto" data-id="element-251">
          Book a free 15-minute consultation to find the right program for your
          athlete.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-stretch sm:items-center max-w-md sm:max-w-none mx-auto" data-id="element-252">
          <Link to="/pricing" className="inline-flex items-center justify-center bg-white text-reach-black font-inter font-semibold px-6 min-h-[44px] md:min-h-[48px] rounded-[4px] transition-all duration-200 ease-out hover:bg-reach-black hover:text-white uppercase tracking-wide text-sm md:text-base hover:-translate-y-[3px]" data-id="element-253">
            Enroll Now
          </Link>
          <Link to="/book" className="btn-secondary-dark sm:w-auto" data-id="element-254">
            Book Free Consultation
          </Link>
        </div>
      </div>
    </section>;
}